import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ChevronDownIcon, CheckCircle2Icon, LockIcon, EyeIcon, EyeOffIcon, XCircleIcon, CheckCircleIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { RegistrationCTAButton } from './RegistrationCTAButton';
type ForgotStep = 'recover_account' | 'verify_otp' | 'set_password' | 'success';
interface ForgotPasswordScreenProps {
  onBack: () => void;
  onComplete: () => void;
}
const ID_TYPES = ['CNIC Number', 'NICOP', 'Passport Number', 'National Tax Number'];
export function ForgotPasswordScreen({
  onBack,
  onComplete
}: ForgotPasswordScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [step, setStep] = useState<ForgotStep>('recover_account');
  const [direction, setDirection] = useState(1);
  // Step 1: Recover Account
  const [idType, setIdType] = useState(ID_TYPES[0]);
  const [showIdDropdown, setShowIdDropdown] = useState(false);
  const [idValue, setIdValue] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  // Step 2: OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpTimer, setOtpTimer] = useState(30);
  // Step 3: Password
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Password validation
  const pwdLength = password.length >= 8 && password.length <= 16;
  const pwdUpper = /[A-Z]/.test(password);
  const pwdLower = /[a-z]/.test(password);
  const pwdNum = /[0-9]/.test(password);
  const pwdSpec = /[!@#$%^&*]/.test(password);
  const pwdValidCount = [pwdLength, pwdUpper, pwdLower, pwdNum, pwdSpec].filter(Boolean).length;
  const pwdsMatch = password.length > 0 && password === confirmPassword;
  // OTP Timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === 'verify_otp' && otpTimer > 0) {
      timer = setInterval(() => setOtpTimer(prev => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [step, otpTimer]);
  const navigateTo = (nextStep: ForgotStep, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  const handleBack = () => {
    if (step === 'recover_account') onBack();else if (step === 'verify_otp') navigateTo('recover_account', -1);else if (step === 'set_password') navigateTo('verify_otp', -1);
  };
  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/[^\d]/g, '').slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    if (digit && index < 5) {
      const nextInput = document.getElementById(`forgot-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (index === 5 && digit) {
      const filled = newOtp.every(d => d !== '');
      if (filled) {
        setTimeout(() => navigateTo('set_password'), 350);
      }
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && index > 0 && !otp[index]) {
      const prev = document.getElementById(`forgot-otp-${index - 1}`);
      prev?.focus();
    }
  };
  const bgStyle = isPlatinum ? 'bg-[#1C1C1E]' : isDark ? 'bg-[#1A0E08]' : 'bg-[#FFF5F0]';
  const textStyle = isPlatinum || isDark ? 'text-white' : 'text-[#1A1A1A]';
  const subtextStyle = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]';
  const inputBgStyle = isPlatinum ? 'bg-[#3A3A3C]' : isDark ? 'bg-[#3D2518]' : 'bg-white';
  const inputBorderStyle = isPlatinum ? 'border-[rgba(160,160,165,0.15)]' : isDark ? 'border-[rgba(243,112,36,0.15)]' : 'border-[#F0E8E3]';
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: 'easeIn'
      }
    })
  };
  return <div className={`w-full min-h-screen flex flex-col relative overflow-hidden ${step === 'success' ? 'bg-white' : bgStyle}`} data-id="element-878">
      {step !== 'success' && <div className="px-6 pt-6 pb-4 flex items-center gap-3 relative z-10" data-id="element-879">
          <button onClick={handleBack} className={`p-2 -ml-2 rounded-full transition-colors ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-880">
            <ArrowLeftIcon className={`w-6 h-6 ${textStyle}`} strokeWidth={2} data-id="element-881" />
          </button>
          <h2 className={textStyle} style={{
        fontFamily: 'Poppins, system-ui, sans-serif',
        fontWeight: 500,
        fontSize: 18
      }} data-id="element-882">
            {step === 'recover_account' ? 'Recover Account' : step === 'verify_otp' ? 'Verify OTP' : 'Set New Password'}
          </h2>
        </div>}

      <div className="flex-1 px-6 flex flex-col relative z-10" data-id="element-883">
        <AnimatePresence mode="wait" custom={direction} data-id="element-884">
          {step === 'recover_account' && <motion.div key="recover_account" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="flex-1 flex flex-col pt-4" data-id="element-885">
              <div className="mb-5 relative" data-id="element-886">
                <label className="mb-2 block" style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 13,
              color: '#661806'
            }} data-id="element-887">
                  ID Type
                </label>
                <button onClick={() => setShowIdDropdown(!showIdDropdown)} className={`w-full relative border ${inputBorderStyle} ${inputBgStyle} flex items-center justify-between px-5`} style={{
              height: 54.5,
              borderRadius: 8
            }} data-id="element-888">
                  <span className={`text-base font-medium ${textStyle}`} data-id="element-889">
                    {idType}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-890" />
                </button>

                <AnimatePresence data-id="element-891">
                  {showIdDropdown && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className={`absolute top-[85px] left-0 right-0 rounded-xl shadow-xl z-20 overflow-hidden border ${inputBgStyle} ${inputBorderStyle}`} data-id="element-892">
                      {ID_TYPES.map(type => <button key={type} onClick={() => {
                  setIdType(type);
                  setShowIdDropdown(false);
                  setIdValue('');
                }} className={`w-full text-left p-4 border-b last:border-0 ${inputBorderStyle} hover:bg-black/5 ${textStyle}`} data-id="element-893">
                          {type}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <div className="mb-5" data-id="element-894">
                <label className="mb-2 block" style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 13,
              color: '#661806'
            }} data-id="element-895">
                  {idType}
                </label>
                <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center px-5`} style={{
              height: 54.5,
              borderRadius: 8
            }} data-id="element-896">
                  <input type="text" value={idValue} onChange={e => setIdValue(e.target.value)} placeholder={`Enter ${idType}`} className={`w-full outline-none text-base bg-transparent font-medium tracking-wider ${textStyle} placeholder:opacity-40`} data-id="element-897" />
                </div>
              </div>

              <div className="mb-5" data-id="element-898">
                <label className="mb-2 block" style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 13,
              color: '#661806'
            }} data-id="element-899">
                  Account Number / IBAN{' '}
                  <span className="opacity-60 font-medium normal-case tracking-normal" data-id="element-900">
                    (Optional)
                  </span>
                </label>
                <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center px-5`} style={{
              height: 54.5,
              borderRadius: 8
            }} data-id="element-901">
                  <input type="text" value={accountNumber} onChange={e => setAccountNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="Enter account no. or IBAN" className={`w-full outline-none text-base bg-transparent font-medium tracking-wider ${textStyle} placeholder:opacity-40`} data-id="element-902" />
                </div>
              </div>

              <div className="mt-auto pb-8 flex justify-center" data-id="element-903">
                <RegistrationCTAButton onClick={() => navigateTo('verify_otp')} disabled={!idValue.trim()} data-id="element-904">
                  Continue
                </RegistrationCTAButton>
              </div>
            </motion.div>}

          {step === 'verify_otp' && <motion.div key="verify_otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="flex-1 flex flex-col pt-4" data-id="element-905">
              <p className={`mb-8 ${subtextStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 14
          }} data-id="element-906">
                We've sent an OTP to your registered mobile number.
              </p>

              <div className="flex justify-between gap-2 mb-8 p-3 rounded-2xl" style={{
            background: isPlatinum ? '#2C2C2E' : isDark ? '#2D1810' : '#F9F9F9'
          }} data-id="element-907">
                {otp.map((digit, index) => <div key={index} className="relative group flex-1" data-id="element-908">
                    <input id={`forgot-otp-${index}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} className={`relative w-full h-14 rounded-2xl text-center text-2xl font-bold outline-none border transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10' : `${inputBorderStyle} ${inputBgStyle}`} ${textStyle}`} data-id="element-909" />
                  </div>)}
              </div>

              <div className="flex flex-col items-center justify-center gap-2" data-id="element-910">
                {otpTimer > 0 ? <div className="flex items-center gap-2" data-id="element-911">
                    <span style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 400,
                fontSize: 13,
                color: '#6E6F72'
              }} data-id="element-912">
                      Resend OTP in{' '}
                      <span style={{
                  color: '#661806',
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-913">
                        0:{String(otpTimer).padStart(2, '0')}
                      </span>
                    </span>
                  </div> : <button onClick={() => setOtpTimer(30)} className="text-[#F37024] font-bold text-sm hover:underline" data-id="element-914">
                    Resend OTP
                  </button>}
              </div>

              <div className="mt-auto pb-8 pt-4 text-center" data-id="element-915">
                <p className={`text-[12px] ${subtextStyle}`} data-id="element-916">
                  Do not share your OTP with anyone
                </p>
              </div>
            </motion.div>}

          {step === 'set_password' && <motion.div key="set_password" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="flex-1 flex flex-col pt-4" data-id="element-917">
              <div className="grid grid-cols-2 gap-2 mb-5" data-id="element-918">
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdLength ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-919">
                  8-16 Characters
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdUpper ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-920">
                  1 Uppercase
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdLower ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-921">
                  1 Lowercase
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdNum ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-922">
                  1 Number
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdSpec ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-923">
                  1 Special Character
                </div>
              </div>

              <label className="mb-2 block" style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 13,
            color: '#661806'
          }} data-id="element-924">
                New Password
              </label>
              <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center px-5 mb-5`} style={{
            height: 54.5,
            borderRadius: 8
          }} data-id="element-925">
                <LockIcon className="w-5 h-5 text-[#F37024] mr-3 shrink-0" data-id="element-926" />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter new password" className={`w-full outline-none text-base tracking-widest bg-transparent font-medium ${textStyle} placeholder:opacity-50 placeholder:text-sm placeholder:font-normal placeholder:tracking-normal`} data-id="element-927" />
                <button onClick={() => setShowPassword(!showPassword)} className="ml-2 shrink-0 p-1" data-id="element-928">
                  {showPassword ? <EyeOffIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-929" /> : <EyeIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-930" />}
                </button>
              </div>

              <label className="mb-2 block" style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 13,
            color: '#661806'
          }} data-id="element-931">
                Confirm New Password
              </label>
              <div className={`relative border ${confirmPassword.length > 0 && pwdsMatch ? 'border-[#22C55E]' : confirmPassword.length > 0 && !pwdsMatch ? 'border-red-500' : inputBorderStyle} ${inputBgStyle} flex items-center px-5`} style={{
            height: 54.5,
            borderRadius: 8
          }} data-id="element-932">
                <LockIcon className="w-5 h-5 text-[#F37024] mr-3 shrink-0" data-id="element-933" />
                <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" className={`w-full outline-none text-base tracking-widest bg-transparent font-medium ${textStyle} placeholder:opacity-50 placeholder:text-sm placeholder:font-normal placeholder:tracking-normal`} data-id="element-934" />
                {confirmPassword.length > 0 && (pwdsMatch ? <CheckCircleIcon className="w-5 h-5 text-[#22C55E] shrink-0 mr-2" data-id="element-935" /> : <XCircleIcon className="w-5 h-5 text-red-500 shrink-0 mr-2" data-id="element-936" />)}
                <button onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="ml-1 shrink-0 p-1" data-id="element-937">
                  {showConfirmPassword ? <EyeOffIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-938" /> : <EyeIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-939" />}
                </button>
              </div>

              {confirmPassword.length > 0 && !pwdsMatch && <p className="text-xs text-red-500 font-medium mt-2 ml-2" data-id="element-940">
                  Passwords do not match
                </p>}

              <div className="mt-auto pb-8 pt-4 flex justify-center" data-id="element-941">
                <RegistrationCTAButton onClick={() => navigateTo('success')} disabled={pwdValidCount < 5 || !pwdsMatch} data-id="element-942">
                  Confirm
                </RegistrationCTAButton>
              </div>
            </motion.div>}

          {step === 'success' && <motion.div key="success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="flex-1 flex flex-col items-center justify-center pt-4" data-id="element-943">
              <div className="absolute inset-0 pointer-events-none overflow-hidden flex justify-center" data-id="element-944">
                {/* Simple CSS Confetti */}
                {Array.from({
              length: 30
            }).map((_, i) => <motion.div key={i} className="absolute w-2 h-2 rounded-sm" style={{
              background: ['#F37024', '#22C55E', '#3B82F6', '#EAB308', '#EC4899'][i % 5],
              top: '-10%',
              left: `${Math.random() * 100}%`
            }} animate={{
              y: ['0vh', '100vh'],
              x: [`0px`, `${(Math.random() - 0.5) * 200}px`],
              rotate: [0, Math.random() * 360 + 360]
            }} transition={{
              duration: 2.5 + Math.random() * 2,
              ease: 'linear',
              repeat: Infinity,
              delay: Math.random() * 2
            }} data-id="element-945" />)}
              </div>

              <motion.div initial={{
            scale: 0.5,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            damping: 15
          }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6 z-10" data-id="element-946">
                <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={2} data-id="element-947" />
              </motion.div>

              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3 text-center z-10" style={{
            fontFamily: 'Poppins, system-ui, sans-serif'
          }} data-id="element-948">
                Password Changed Successfully!
              </h2>

              <p className="text-[#5A5B5E] text-center mb-10 max-w-[280px] z-10" style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontSize: 14
          }} data-id="element-949">
                Your password has been updated. You can now log in with your new
                password.
              </p>

              <div className="w-full mt-auto pb-8 z-10 flex justify-center" data-id="element-950">
                <RegistrationCTAButton onClick={onComplete} data-id="element-951">
                  Go to Login
                </RegistrationCTAButton>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}